import { useSettings } from '@wix/tpa-settings/react';
import React from 'react';
import {
  PaymentMethod,
  type PaymentOption,
  PaymentType,
  ReservedPaymentOptionIds,
  ReservedPaymentOptionLabels,
} from '../../../../../../types/types';
import { DepositOrFullAmountOptionIds } from '../../../../../../utils/payment/paymentOptions/utils';
import { getContent } from '../../../../../../utils/content/content';
import type { SlotService } from '../../../../../../utils/state/types';
import { useFormActions } from '../../../../Hooks/useFormActions';
import settingsParams from '../../../../settingsParams';
import { ServicePaymentDropdown } from './ServicePaymentDropdown';
import { ServicePaymentRadioButtons } from './ServicePaymentRadioButtons';

export type PaymentFormatProps = {
  service: SlotService;
  paymentOptions: PaymentOption[];
  label: string;
  selectedId: string;
  onSelect: (id: string) => void;
  getPaymentOptionLabel: (option: PaymentOption) => string;
  buildPaymentOptionId: (option: PaymentOption) => string;
};

export type ServicePaymentOptionsProps = {
  service: SlotService;
  paymentOptions: PaymentOption[];
  label: string;
  isSingleService: boolean;
};

export const ServicePaymentOptions: React.FC<ServicePaymentOptionsProps> = ({
  service,
  paymentOptions,
  label,
  isSingleService,
}) => {
  const { setServiceSelectedPaymentOption } = useFormActions();
  const settings = useSettings();

  const buildPaymentOptionId = (paymentOption: PaymentOption) => {
    return `${paymentOption.id}:${paymentOption.label}`;
  };

  const getPaymentOptionLabel = (paymentOption: PaymentOption) => {
    let optionLabel;
    switch (paymentOption.type) {
      case PaymentType.SINGLE_SESSION: {
        if (paymentOption.label === ReservedPaymentOptionLabels.ONLINE) {
          optionLabel = getContent({
            settings,
            settingsParam: settingsParams.payOnlineText,
          });
        } else if (
          paymentOption.label === ReservedPaymentOptionLabels.OFFLINE
        ) {
          optionLabel = getContent({
            settings,
            settingsParam: settingsParams.payOfflineText,
          });
        } else if (paymentOption.label === ReservedPaymentOptionLabels.FREE) {
          optionLabel = getContent({
            settings,
            settingsParam: settingsParams.freeText,
          });
        } else {
          optionLabel = paymentOption.label;
        }
        break;
      }
      case PaymentType.BUY_PLAN:
      case PaymentType.USE_PLAN: {
        optionLabel = paymentOption.label;
        break;
      }
      default:
        optionLabel = paymentOption.label;
    }

    return optionLabel.toString();
  };

  const findSelectedPaymentOption = (id: string) => {
    return (paymentOptions || []).find(
      (paymentOption) => id === buildPaymentOptionId(paymentOption),
    )!;
  };

  const getSelectedId = () => {
    const selectedPaymentOptionId = service.selectedPaymentOption.id;
    const selectedPaymentType = service.selectedPaymentType;
    let foundOption;
    if (selectedPaymentOptionId === ReservedPaymentOptionIds.BuyAPricingPlan) {
      foundOption = paymentOptions.find(
        (option) => option.type === PaymentType.BUY_PLAN,
      );
    } else if (
      selectedPaymentOptionId === ReservedPaymentOptionIds.SingleSession
    ) {
      if (selectedPaymentType === PaymentMethod.ONLINE) {
        foundOption = paymentOptions.find(
          (option) =>
            option.type === PaymentType.SINGLE_SESSION &&
            (option.id === DepositOrFullAmountOptionIds.Deposit ||
              option.id === DepositOrFullAmountOptionIds.FullAmount),
        );
        if (!foundOption) {
          foundOption = paymentOptions.find(
            (option) =>
              option.type === PaymentType.SINGLE_SESSION &&
              option.label === ReservedPaymentOptionLabels.ONLINE,
          );
        }
      } else if (selectedPaymentType === PaymentMethod.OFFLINE) {
        foundOption = paymentOptions.find(
          (option) =>
            option.type === PaymentType.SINGLE_SESSION &&
            option.label === ReservedPaymentOptionLabels.OFFLINE,
        );
      } else {
        foundOption = paymentOptions.find(
          (option) => option.type === PaymentType.SINGLE_SESSION,
        );
      }
    } else {
      foundOption = paymentOptions.find(
        (option) => option.id === selectedPaymentOptionId,
      );
    }

    if (foundOption) {
      return buildPaymentOptionId(foundOption);
    }
  };

  const [selectedId, setSelectedId] = React.useState<string>(
    () => getSelectedId()!,
  );

  const onSelect = (id: string) => {
    setSelectedId(id);
    setServiceSelectedPaymentOption(service, findSelectedPaymentOption(id));
  };

  if (!selectedId) {
    return null;
  }

  const formatProps: PaymentFormatProps = {
    service,
    paymentOptions,
    label,
    selectedId,
    onSelect,
    getPaymentOptionLabel,
    buildPaymentOptionId,
  };

  const ServicePaymentComponent = isSingleService
    ? ServicePaymentRadioButtons
    : ServicePaymentDropdown;
  return <ServicePaymentComponent {...formatProps} />;
};
